// Debug-Fenster zur Tourenberechnung.
//
// Zeigt die letzte Aufzeichnung eines Technikertages als lesbaren Bericht und erlaubt, alles mit
// einem Klick in die Zwischenablage zu legen oder als Datei zu sichern. Das JSON im kopierten Text
// ist zugleich das Fixture-Format des Regressionstests (siehe tour-optimizer.fixtures.spec.ts).
//
// Geöffnet wird das Fenster über TourDebugStore.openRequests — entweder aus der Fehlermeldung der
// Tourenberechnung oder über den Käfer-Knopf in der Timeline-Zeile des jeweiligen Technikertages.
// Beide Wege reichen den Schlüssel des Technikertages mit, damit immer genau der Tag erscheint, zu
// dem geklickt wurde.

import { Component, DestroyRef, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faCheck, faCopy, faDownload, faTimes } from "@fortawesome/free-solid-svg-icons";
import { TourDebugStore } from "../classes/tour-optimizer/tour-debug-recorder";
import {
  buildTourDebugClipboardText,
  buildTourDebugDiagnosis,
  buildTourDebugFileName,
  buildTourDebugReport,
} from "../classes/tour-optimizer/tour-debug-report";
import type { TourDebugSnapshot } from "../classes/tour-optimizer/tour-debug.types";

@Component({
  selector: "hawk-tour-debug-dialog",
  templateUrl: "./tour-debug-dialog.component.html",
  standalone: true,
  imports: [FontAwesomeModule],
})
export class TourDebugDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  faCopy = faCopy;
  faDownload = faDownload;
  faTimes = faTimes;
  faCheck = faCheck;

  /** Angezeigte Aufzeichnung; null = Fenster geschlossen */
  readonly snapshot = signal<TourDebugSnapshot | null>(null);
  readonly copied = signal<boolean>(false);

  readonly diagnosis = computed(() => {
    const snapshot = this.snapshot();
    return snapshot ? buildTourDebugDiagnosis(snapshot) : "";
  });

  readonly report = computed(() => {
    const snapshot = this.snapshot();
    return snapshot ? buildTourDebugReport(snapshot) : "";
  });

  readonly hasProblem = computed(() => {
    const snapshot = this.snapshot();
    return !!snapshot && (!snapshot.result.route_found || snapshot.result.errors.length > 0);
  });

  constructor() {
    TourDebugStore.openRequests.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(key => {
      this.open(key);
    });
  }

  /**
   * Öffnet die Aufzeichnung zum Schlüssel; ohne Schlüssel die jüngste vorhandene.
   *
   * Zu einem angefragten Schlüssel wird bewusst NICHT auf die jüngste Aufzeichnung ausgewichen —
   * sonst zeigte ein Klick in der Timeline-Zeile stillschweigend die Daten eines fremden Tages.
   */
  open(key: string | null): void {
    const snapshot = key ? TourDebugStore.all().find(entry => this.keyOf(entry) === key) : TourDebugStore.last();
    if (!snapshot) return;
    this.copied.set(false);
    this.snapshot.set(snapshot);
  }

  close(): void {
    this.snapshot.set(null);
  }

  private keyOf(snapshot: TourDebugSnapshot): string {
    return TourDebugStore.key(snapshot.head.technician_id, snapshot.head.day_timestamp);
  }

  async copyToClipboard(): Promise<void> {
    const snapshot = this.snapshot();
    if (!snapshot) return;

    const text = buildTourDebugClipboardText(snapshot);
    try {
      await navigator.clipboard.writeText(text);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Ältere Browser und Seiten ohne sicheren Kontext kennen navigator.clipboard nicht
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    }
  }

  downloadJson(): void {
    const snapshot = this.snapshot();
    if (!snapshot) return;

    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = buildTourDebugFileName(snapshot);
    link.click();
    URL.revokeObjectURL(url);
  }
}
